import React, { useState, useEffect } from 'react';
import { Wallet } from 'lucide-react';
import { useWalletUser } from '../hooks/useWalletUser';

// Define wallet type based on the requirements
interface WalletInfo {
  address: string;
  label?: string;
  isActive?: boolean;
}

interface WalletSelectorProps {
  onSelectWallet: (walletAddress: string) => void;
  selectedWalletAddress?: string;
  className?: string;
}

const WalletSelector: React.FC<WalletSelectorProps> = ({
  onSelectWallet,
  selectedWalletAddress,
  className = ''
}) => {
  const userData = useWalletUser();
  const [isOpen, setIsOpen] = useState(false);
  
  // Mock connected wallets for now - in a real implementation, this would come from a hook
  // that provides the user's connected wallets
  const [connectedWallets, setConnectedWallets] = useState<WalletInfo[]>([]);
  
  // Load connected wallets when user data changes
  useEffect(() => {
    if (userData.walletAddress) {
      // For now, we'll just use the current wallet from useWalletUser
      // In a real implementation, you'd fetch all the user's wallets
      setConnectedWallets([{
        address: userData.walletAddress,
        label: userData.name,
        isActive: true
      }]);
    }
  }, [userData]);

  // Use active wallet as default selected wallet if none provided
  useEffect(() => {
    if (!selectedWalletAddress && userData.walletAddress) {
      onSelectWallet(userData.walletAddress);
    }
  }, [userData, selectedWalletAddress, onSelectWallet]);

  // Handle wallet selection
  const handleSelectWallet = (address: string) => {
    onSelectWallet(address);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 hover:bg-slate-700/60 rounded-md border border-white/10 text-sm"
      >
        <Wallet size={14} />
        <span className="truncate max-w-[120px]">
          {selectedWalletAddress ? (
            <>
              {selectedWalletAddress.substring(0, 4)}...
              {selectedWalletAddress.substring(selectedWalletAddress.length - 4)}
            </>
          ) : (
            'Select Wallet'
          )}
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-slate-800 rounded-md border border-white/10 shadow-lg overflow-hidden">
          <div className="px-2 py-1.5 border-b border-white/10 text-xs font-medium text-gray-400">
            Your Wallets
          </div>

          <div className="max-h-48 overflow-y-auto">
            {userData.isLoading ? (
              <div className="p-3 text-center text-sm text-gray-400">
                Loading wallets...
              </div>
            ) : connectedWallets.length > 0 ? (
              connectedWallets.map((wallet: WalletInfo) => (
                <button
                  key={wallet.address}
                  onClick={() => handleSelectWallet(wallet.address)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${
                    selectedWalletAddress === wallet.address ? 'bg-white/10' : ''
                  }`}
                >
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <div className="flex-1 truncate">
                    {wallet.address.substring(0, 6)}...
                    {wallet.address.substring(wallet.address.length - 4)}
                  </div>
                  {wallet.isActive && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                      Active
                    </span>
                  )}
                </button>
              ))
            ) : (
              <div className="p-3 text-center text-sm text-gray-400">
                No wallets connected
              </div>
            )}
          </div>

          <div className="px-3 py-2 border-t border-white/10">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full text-center text-xs text-blue-400 hover:text-blue-300"
            >
              Connect New Wallet
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletSelector; 